import {
  Fragment,
  useEffect,
  useState,
  SyntheticEvent,
  useRef,
  useCallback,
} from 'react';
import { debounce } from '@mui/material';
import {
  AutocompleteProps,
  AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { BaseParams } from 'api/api.types';
import { throttle } from 'lodash';
import { useTranslation } from 'react-i18next';
import { AutocompleteTextField, StyledAutocomplete } from './Inputs.css';

interface Props<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> extends Omit<
    AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    'renderInput' | 'options'
  > {
  label?: string;
  hiddenLabel?: boolean;
  name?: string;
  error?: boolean;
  onFetch?: (baseParams: BaseParams) => Promise<T[]>;
}

export function AutocompleteInput<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(props: Props<T, Multiple, DisableClearable, FreeSolo>) {
  const { label, hiddenLabel, name, error, onFetch, ...autocompleteProps } =
    props;
  const { onInputChange, onOpen, onClose, ...restAutocompleteProps } =
    autocompleteProps;

  const { t } = useTranslation(['misc']);

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [filterText, setFilterText] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const isFetching = useRef<boolean>(false);
  const isLastPageFetched = useRef<boolean>(false);
  const autocompleteRef = useRef<HTMLElement>(null);

  const fetchData = useCallback(
    async ({ filterText, pageNumber }: Required<BaseParams>) => {
      if (!onFetch) return undefined;

      const data = await onFetch({ filterText, pageNumber });

      // NOTE: I should check if less than resultSize value, but it appears to be broken for some of the endpoints.
      if (data.length === 0) {
        isLastPageFetched.current = true;
      }

      return data;
    },
    [onFetch],
  );

  const fetchNextPage = useCallback(
    async ({ filterText, pageNumber }: Required<BaseParams>) => {
      try {
        isFetching.current = true;

        const data = await fetchData({ filterText, pageNumber });
        if (data) {
          setOptions((prev) => [...prev, ...data]);
          setPageNumber(pageNumber);
        }

        return data;
      } finally {
        isFetching.current = false;
      }
    },
    [fetchData, setOptions, setPageNumber],
  );

  const handleInputSearch = debounce(
    useCallback(
      async (value: string, reason: AutocompleteInputChangeReason) => {
        if (!open || reason === 'reset') return;

        try {
          isFetching.current = true;
          isLastPageFetched.current = false;

          const data = await fetchData({ filterText: value, pageNumber: 1 });

          if (data) {
            setOptions(data);
            setFilterText(value);
            setPageNumber(1);
          }
        } finally {
          isFetching.current = false;
        }
      },
      [open, fetchData, setFilterText, setPageNumber, setOptions],
    ),
    800,
  );

  const handleScrollBottom = useCallback(
    async (event: SyntheticEvent) => {
      if (
        !event.currentTarget ||
        isFetching.current ||
        isLastPageFetched.current
      )
        return;

      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;

      const bottomOffset = 100;
      const shouldFetchNextPage =
        scrollTop + clientHeight >= scrollHeight - bottomOffset;

      if (!shouldFetchNextPage) return;

      await fetchNextPage({ filterText, pageNumber: pageNumber + 1 });
    },
    [filterText, pageNumber, isFetching, isLastPageFetched, fetchNextPage],
  );

  useEffect(() => {
    // NOTE: Fetches pages till scrollbar appears or last page is fetched.
    if (
      !open ||
      isLastPageFetched.current ||
      !autocompleteRef.current ||
      !autocompleteRef.current.parentElement
    )
      return;

    const listboxNode =
      autocompleteRef.current.parentElement.querySelector('[role="listbox"]');

    // NOTE: Checks if there's a list and if scroll is possible.
    if (listboxNode && listboxNode.scrollHeight > listboxNode.clientHeight)
      return;

    (async () => {
      await fetchNextPage({ filterText, pageNumber: pageNumber + 1 });
    })();
  }, [
    pageNumber,
    filterText,
    fetchNextPage,
    open,
    isLastPageFetched,
    autocompleteRef,
  ]);

  useEffect(() => {
    if (open) {
      // NOTE: initial fetch on menu open
      (async () => {
        try {
          isFetching.current = true;

          const data = await fetchData({ filterText: '', pageNumber: 1 });
          if (data) {
            setOptions(data);
            setPageNumber(1);
          }
        } finally {
          isFetching.current = false;
        }
      })();
    } else {
      // NOTE: state reset after onClose callback
      setOptions([]);
      setFilterText('');
      setPageNumber(0);
      isLastPageFetched.current = false;
    }
  }, [open, fetchData, setOptions, setFilterText, setPageNumber]);

  return (
    <StyledAutocomplete
      ref={autocompleteRef}
      padding='dense'
      disablePortal
      open={open}
      loadingText={t('combobox.searching', { ns: 'misc' })}
      noOptionsText={t('combobox.noMatches', { ns: 'misc' })}
      // @ts-ignore - because of styled-components
      filterOptions={(x) => x} // disables filtering client side
      filterSelectedOptions // helps with unintended removal of options
      clearOnBlur
      ListboxProps={{
        style: {
          maxHeight: '250px',
        },
        onScroll: throttle(handleScrollBottom, 100, {
          trailing: false,
          leading: true,
        }),
      }}
      {...restAutocompleteProps}
      onInputChange={(e, value, reason) => {
        handleInputSearch(value, reason);
        onInputChange && onInputChange(e, value, reason);
      }}
      onOpen={(e) => {
        setOpen(true);
        onOpen && onOpen(e);
      }}
      onClose={(e, reason) => {
        setOpen(false);
        onClose && onClose(e, reason);
      }}
      options={options}
      renderInput={(params) => (
        <AutocompleteTextField
          padding='dense'
          {...params}
          name={name}
          label={!hiddenLabel ? label : null}
          hiddenLabel={hiddenLabel}
          error={error}
          variant='outlined'
          margin='dense'
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {autocompleteProps.loading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
        />
      )}
    />
  );
}
